import dayjs from 'dayjs'

import axiosNew from '../../helpers/axios'
import { PRIVATE_ROUTES } from '../../routes'
import { parseStepLabel, parseJourneyLabel, mapApiMetaToCardFields, mapCardColorFields, mapStoryStatusFields, resolveBoardItemMatchesFilter, stripBubbledCommentMeta, isMetaFlagTrue } from './components/ProjectStoryMapBoard/utils'

export const formatReleaseDateRange = (start, end) => {
    const formatValue = (value) => {
        if (value === null || value === undefined || value === '') return ''
        const parsed = dayjs(value)
        return parsed.isValid() ? parsed.format('D MMM YYYY') : ''
    }

    const startLabel = formatValue(start)
    const endLabel = formatValue(end)

    if (startLabel && endLabel) return `${startLabel} - ${endLabel}`
    return startLabel || endLabel || ''
}

export const mapStoryCardFields = mapApiMetaToCardFields

export const EMPTY_STORY_MAP_BOARD_FILTERS = {
    search: '',
    commentFilter: 'all',
    from: null,
    to: null,
    storyStatus: [],
    hasComments: false,
    hasAttachment: false,
    hasDescription: false,
    journeyId: null,
    releaseId: null,
}

export const hasActiveStoryMapBoardFilters = (filters = EMPTY_STORY_MAP_BOARD_FILTERS) => {
    const f = filters || EMPTY_STORY_MAP_BOARD_FILTERS
    const hasPresetCommentFilter =
        f.commentFilter &&
        f.commentFilter !== 'all' &&
        f.commentFilter !== 'custom'
    const hasCustomCommentRange = Boolean(f.from && f.to)

    return Boolean(
        f.search?.trim() ||
            hasPresetCommentFilter ||
            hasCustomCommentRange ||
            f.commentFilter === 'custom' ||
            (Array.isArray(f.storyStatus) && f.storyStatus.length > 0) ||
            f.hasComments ||
            f.hasAttachment ||
            f.hasDescription ||
            f.journeyId ||
            f.releaseId
    )
}

export const isStoryMapBoardFilterRequestReady = (
    filters = EMPTY_STORY_MAP_BOARD_FILTERS
) => {
    const f = filters || EMPTY_STORY_MAP_BOARD_FILTERS
    if (f.commentFilter === 'custom') {
        return Boolean(f.from && f.to)
    }
    return true
}

export const buildStoryMapListQuery = (filters = EMPTY_STORY_MAP_BOARD_FILTERS) => {
    const params = new URLSearchParams()
    const f = filters || EMPTY_STORY_MAP_BOARD_FILTERS

    const search = String(f.search ?? '').trim()
    if (search) {
        params.set('search', search)
    }

    if (f.from && f.to) {
        const fromDate = dayjs(f.from)
        const toDate = dayjs(f.to)
        if (fromDate.isValid() && toDate.isValid()) {
            params.set('from', fromDate.startOf('day').toISOString())
            params.set('to', toDate.endOf('day').toISOString())
        }
    } else if (
        f.commentFilter &&
        f.commentFilter !== 'all' &&
        f.commentFilter !== 'custom'
    ) {
        params.set('commentFilter', f.commentFilter)
    }

    if (Array.isArray(f.storyStatus) && f.storyStatus.length > 0) {
        params.set('storyStatus', f.storyStatus.join(','))
    }

    if (f.hasComments) params.set('hasComments', 'true')
    if (f.hasAttachment) params.set('hasAttachment', 'true')
    if (f.hasDescription) params.set('hasDescription', 'true')
    if (f.journeyId) params.set('journeyId', String(f.journeyId))
    if (f.releaseId) params.set('releaseId', String(f.releaseId))

    return params
}

/** Normalize API error payloads so React never receives `{ error: "..." }` as a child. */
export const formatStoryMapMessage = (message, fallback = 'Something went wrong') => {
    if (message == null || message === '') return fallback
    if (typeof message === 'string') return message
    if (typeof message === 'object') {
        if (typeof message.error === 'string') return message.error
        if (typeof message.message === 'string') return message.message
        try {
            return JSON.stringify(message)
        } catch {
            return fallback
        }
    }
    return String(message)
}

export const mapStoryMapDetailToBoard = (detail, requestFilters = EMPTY_STORY_MAP_BOARD_FILTERS) => {
    if (!detail) return null

    const isFilterActive = Boolean(detail.isFilterActive)
    const appliedFilters = detail.appliedFilters ?? requestFilters ?? {}

    const journeys = (detail.journeys ?? []).map((journey, journeyIndex) => {
        const journeyNumber = journeyIndex + 1
        const journeyMeta = stripBubbledCommentMeta(
            journey,
            mapStoryCardFields(journey),
            appliedFilters
        )

        return {
            id: String(journey._id),
            title: `${journeyNumber}. ${journey.name || 'Untitled journey'}`,
            ...journeyMeta,
            description: journey.description || '',
            matchesFilter: resolveBoardItemMatchesFilter(
                journey,
                'journey',
                appliedFilters
            ),
            ...(journey.todayComment !== undefined
                ? { todayComment: Boolean(journey.todayComment) }
                : {}),
            steps: (journey.steps ?? []).map((step, stepIndex) => {
                const stepMeta = stripBubbledCommentMeta(
                    step,
                    mapStoryCardFields(step),
                    appliedFilters
                )

                return {
                    id: String(step._id),
                    title: `${journeyNumber}.${stepIndex + 1} ${
                        step.name || 'Untitled step'
                    }`,
                    ...stepMeta,
                    ...mapCardColorFields(step),
                    matchesFilter: resolveBoardItemMatchesFilter(
                        step,
                        'step',
                        appliedFilters
                    ),
                    ...(step.todayComment !== undefined
                        ? { todayComment: Boolean(step.todayComment) }
                        : {}),
                }
            }),
        }
    })

    const releases = (detail.release ?? detail.releases ?? []).map(
        (release, releaseIndex) => ({
            id: String(release._id),
            title: release.name || `Release ${releaseIndex + 1}`,
            description: release.description || '',
            start: release.start ?? null,
            end: release.end ?? null,
            dateRange: formatReleaseDateRange(release.start, release.end),
            ...mapStoryCardFields(release),
            isCollapsed: isMetaFlagTrue(release.isCollapsed),
            matchesFilter: Boolean(release.matchesFilter),
            stories: (release.stories ?? []).map((story) => ({
                id: String(story._id),
                title: story.name || 'Untitled story',
                stepId: story.stepId ? String(story.stepId) : story.stepId,
                ...mapStoryCardFields(story),
                ...mapCardColorFields(story),
                ...mapStoryStatusFields(story),
                matchesFilter: resolveBoardItemMatchesFilter(
                    story,
                    'story',
                    appliedFilters
                ),
            })),
        })
    )

    return {
        id: String(detail._id),
        title: detail.name || 'Untitled Story Map',
        isFilterActive,
        appliedFilters: detail.appliedFilters ?? null,
        journeys,
        releases,
    }
}

const mapStoryMapListItem = (item) => ({
    id: item?._id,
    title: item?.name || 'Untitled Story Map',
    journeysCount: item?.journeysCount ?? 0,
    stepsCount: item?.stepsCount ?? 0,
    releasesCount: item?.releaseCount ?? 0,
    storiesCount: item?.storiesCount ?? 0,
    authorName: item?.createdBy?.name || 'Unknown',
    createdDate: item?.createdAt
        ? dayjs(item.createdAt).format('D MMM YYYY')
        : '',
})

export const listStoryMapsByProject = async ({
    storyMapId,
    limit = 25,
    skip = 0,
    search = '',
} = {}) => {
    try {
        const params = new URLSearchParams({
            limit: String(limit),
            skip: String(skip), 
        })

        const trimmedSearch = search.trim()
        if (trimmedSearch) {
            params.set('search', trimmedSearch)
        }

        const response = await axiosNew({
            url: `/support/storyMap/list-by-project/${storyMapId}?${params.toString()}`,
            method: 'get',
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? true,
            isNext: payload.isNext ?? false,
            data: (payload.data ?? []).map(mapStoryMapListItem),
            message: payload.message,
        }
    } catch (error) {
        return {
            success: false,
            isNext: false,
            data: [],
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to load story maps',
        }
    }
}

export const getStoryMapDetail = async (
    storyMapId,
    filters = EMPTY_STORY_MAP_BOARD_FILTERS
) => {
    try {
        const params = buildStoryMapListQuery(filters)
        const query = params.toString()
        const url = `support/storyMap/list/${storyMapId}${query ? `?${query}` : ''}`

        const response = await axiosNew({
            url,
            method: 'get',
        })

        const payload = response?.data ?? {}
        const detail = payload.result ?? payload.data

        if (!detail?._id) {
            return {
                success: false,
                board: null,
                message: formatStoryMapMessage(payload.message, 'Story map not found'),
            }
        }

        return {
            success: payload.success ?? true,
            board: mapStoryMapDetailToBoard(detail, filters),
            message: formatStoryMapMessage(payload.message, ''),
        }
    } catch (error) {
        return {
            success: false,
            board: null,
            message: formatStoryMapMessage(
                error?.response?.data?.message,
                formatStoryMapMessage(error?.message, 'Failed to load story map')
            ),
        }
    }
}

export const updateStoryMap = async (storyMapId, { name } = {}) => {
    try {
        const data = {}
        if (name !== undefined) data.name = name

        const response = await axiosNew({
            url: `support/storyMap/edit/${storyMapId}`,
            method: 'put',
            data,
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result),
            result: payload.result ?? payload.data,
            message: payload.message ?? 'Story map updated successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to update story map',
        }
    }
}

export const updateStoryMapIndex = async (
    cardId,
    { module, newIndex } = {}
) => {
    try {
        const response = await axiosNew({
            url: `support/storyMap/update-index/${cardId}`,
            method: 'put',
            data: {
                module,
                newIndex: Number(newIndex),
            },
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result ?? payload.data),
            result: payload.result ?? payload.data,
            message: payload.message ?? 'Card position updated successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to update card position',
        }
    }
}

export const isMongoObjectId = (id) => /^[a-f\d]{24}$/i.test(String(id ?? ''))

export const extractPersistedId = (value) => {
    if (value == null || value === '') return ''
    if (typeof value === 'string') return value
    if (typeof value === 'object') {
        return String(value._id ?? value.id ?? '')
    }
    return ''
}

export const createJourney = async ({ storyMapId, name = '' } = {}) => {
    try {
        const response = await axiosNew({
            url: 'support/storyMap/journey/add',
            data: {
                name,
                storyMapId,
            },
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result),
            result: payload.result ?? payload.data,
            message: payload.message ?? 'Journey created successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to create journey',
        }
    }
}

export const updateRelease = async (
    releaseId,
    { name, description, start, end, isCollapsed } = {}
) => {
    try {
        const data = {}
        if (name !== undefined) data.name = name
        if (description !== undefined) data.description = description
        if (start !== undefined) data.start = start
        if (end !== undefined) data.end = end
        if (isCollapsed !== undefined) data.isCollapsed = Boolean(isCollapsed)

        const response = await axiosNew({
            url: `support/storyMap/release/edit/${releaseId}`,
            method: 'put',
            data,
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result),
            result: payload.result ?? payload.data,
            message: payload.message ?? 'Release updated successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to update release',
        }
    }
}

export const updateJourney = async (journeyId, { name, description } = {}) => {
    try {
        const data = {}
        if (name !== undefined) data.name = name
        if (description !== undefined) data.description = description

        const response = await axiosNew({
            url: `support/storyMap/journey/edit/${journeyId}`,
            method: 'put',
            data,
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result),
            result: payload.result ?? payload.data,
            message: payload.message ?? 'Journey updated successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to update journey',
        }
    }
}

export const createStep = async ({
    storyMapId,
    journeyId,
    name = '',
    index,
    setIndex = true,
} = {}) => {
    try {
        const response = await axiosNew({
            url: 'support/storyMap/step/add',
            data: {
                name,
                storyMapId,
                journeyId,
                index,
                setIndex,
            },
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result),
            result: payload.result ?? payload.data,
            message: payload.message ?? 'Step created successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to create step',
        }
    }
}

export const updateStep = async (
    stepId,
    { name, journeyId, description, color, index } = {}
) => {
    try {
        const data = {}

        if (name !== undefined) data.name = name
        if (journeyId !== undefined) data.journeyId = journeyId
        if (description !== undefined) data.description = description
        if (color !== undefined) data.color = color
        if (index !== undefined) data.index = Number(index)

        const response = await axiosNew({
            url: `support/storyMap/step/edit/${stepId}`,
            method: 'put',
            data,
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result),
            result: payload.result ?? payload.data,
            message: payload.message ?? 'Step updated successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to update step',
        }
    }
}

export const createRelease = async ({
    name,
    storyMapId,
    projectId,
    journeyId = '',
    index = 0,
} = {}) => {
    try {
        const response = await axiosNew({
            url: 'support/storyMap/release/add',
            data: {
                name,
                storyMapId,
                journeyId,
                projectId,
                index,
            },
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result),
            result: payload.result ?? payload.data,
            message: payload.message ?? 'Release created successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to create release',
        }
    }
}

export const createStory = async ({
    name = '',
    storyMapId,
    projectId,
    releaseId,
    stepId,
    journeyId,
    index,
} = {}) => {
    try {
        const response = await axiosNew({
            url: 'support/storyMap/story/add',
            data: {
                name,
                storyMapId,
                projectId,
                releaseId,
                stepId,
                journeyId,
                index,
            },
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result),
            result: payload.result ?? payload.data,
            message: payload.message ?? 'Story created successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to create story',
        }
    }
}

export const updateStory = async (
    storyId,
    { name, releaseId, stepId, description, color, status, index } = {}
) => {
    try {
        const data = {}

        if (name !== undefined) data.name = name
        if (releaseId !== undefined) data.releaseId = releaseId
        if (stepId !== undefined) data.stepId = stepId
        if (description !== undefined) data.description = description
        if (color !== undefined) data.color = color
        if (status !== undefined) data.status = status
        if (index !== undefined) data.index = Number(index)

        const response = await axiosNew({
            url: `support/storyMap/story/edit/${storyId}`,
            method: 'put',
            data,
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result),
            result: payload.result ?? payload.data,
            message: payload.message ?? 'Story updated successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to update story',
        }
    }
}

export const deleteJourney = async (journeyId) => {
    try {
        const response = await axiosNew({
            url: `support/storyMap/journey/delete/${journeyId}`,
            method: 'delete',
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result),
            result: payload.result ?? payload.data,
            message: payload.message ?? 'Journey deleted successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to delete journey',
        }
    }
}

export const deleteStep = async (stepId) => {
    try {
        const response = await axiosNew({
            url: `support/storyMap/step/delete/${stepId}`,
            method: 'delete',
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result),
            result: payload.result ?? payload.data,
            message: payload.message ?? 'Step deleted successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to delete step',
        }
    }
}

export const deleteStory = async (storyId) => {
    try {
        const response = await axiosNew({
            url: `support/storyMap/story/delete/${storyId}`,
            method: 'delete',
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result),
            result: payload.result ?? payload.data,
            message: payload.message ?? 'Story deleted successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to delete story',
        }
    }
}

export const deleteRelease = async (releaseId) => {
    try {
        const response = await axiosNew({
            url: `support/storyMap/release/delete/${releaseId}`,
            method: 'delete',
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result),
            result: payload.result ?? payload.data,
            message: payload.message ?? 'Release deleted successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to delete release',
        }
    }
}

export const STORY_MAP_ATTACHMENT_ACCEPT =
    '.png,.jpg,.jpeg,.gif,.svg,.webp,.pdf,.csv'

const STORY_MAP_ATTACHMENT_EXTENSIONS = new Set([
    '.png',
    '.jpg',
    '.jpeg',
    '.gif',
    '.svg',
    '.webp',
    '.pdf',
    '.csv',
])

export const getStoryMapAttachmentDisplayName = (fileName = '') => {
    const base = String(fileName).split('/').pop()
    const match = base.match(/^[a-f0-9]{24}_\d+_(.+)$/i)
    if (match) return match[1].replace(/_/g, ' ')
    return base || 'Attachment'
}

export const getStoryMapAttachmentModule = (cardType) => {
    if (cardType === 'story') return 'story'
    if (cardType === 'journey') return 'journey'
    if (cardType === 'release') return 'release'
    return 'step'
}

export const isAllowedStoryMapAttachment = (file) => {
    if (!file?.name) return false
    const extension = `.${String(file.name).split('.').pop()?.toLowerCase()}`
    return STORY_MAP_ATTACHMENT_EXTENSIONS.has(extension)
}

const mapGetCardAttachments = (attachments = []) =>
    attachments.map((item, index) => {
        const fileName = item?.fileName || ''
        return {
            id: String(fileName || `att-${index}`),
            name: getStoryMapAttachmentDisplayName(fileName) || 'Attachment',
            type: 'file',
            url: item?.url,
            fileName,
        }
    })

export const getStoryMapAttachmentUploadUrls = async (
    cardId,
    { module, files } = {}
) => {
    try {
        const response = await axiosNew({
            url: `support/storyMap/attachments/get-attachment-url/${cardId}`,
            method: 'post',
            data: {
                module,
                fileName: files,
            },
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result),
            result: payload.result ?? [],
            message: payload.message ?? 'Upload URL generated successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: [],
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to upload attachment',
        }
    }
}

export const deleteStoryMapAttachment = async (
    cardId,
    { module, fileName } = {}
) => {
    try {
        const params = new URLSearchParams({
            module: String(module),
            fileName: String(fileName),
        })

        const response = await axiosNew({
            url: `support/storyMap/attachments/delete-attachment/${cardId}?${params.toString()}`,
            method: 'delete',
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? true,
            message: payload.message ?? 'Attachment deleted successfully',
        }
    } catch (error) {
        return {
            success: false,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to delete attachment',
        }
    }
}

export const mapGetCardToBoardFields = (
    detail,
    module,
    { existingTitle } = {}
) => {
    if (!detail) return {}

    const metaFields = mapStoryCardFields(detail)
    const attachments = mapGetCardAttachments(detail.attachments)
    const description =
        typeof detail.description === 'string' ? detail.description : ''
    const hasDescription =
        metaFields.hasDescription ||
        Boolean(
            String(description)
                .replace(/<[^>]*>/g, '')
                .trim()
        )

    const base = {
        ...metaFields,
        ...mapCardColorFields(detail),
        description,
        hasDescription,
        showDescriptionIcon:
            metaFields.showDescriptionIcon || hasDescription || undefined,
        showCommentsIcon: metaFields.showCommentsIcon || undefined,
        showAttachmentIcon:
            metaFields.showAttachmentIcon ||
            attachments.length > 0 ||
            undefined,
        attachments,
        attachmentsCount: attachments.length || metaFields.attachmentsCount || 0,
        hasAttachment:
            metaFields.hasAttachment || attachments.length > 0 || undefined,
        ...(detail.createdAt
            ? {
                  createdDate: dayjs(detail.createdAt).format('D MMM YYYY'),
              }
            : {}),
        ...(detail.createdBy?.name
            ? { authorName: detail.createdBy.name }
            : {}),
    }

    if (attachments.length > 0) {
        base.hasAttachment = true
        base.showAttachmentIcon = true
    }

    if (module === 'step') {
        const { version } = parseStepLabel(existingTitle || '')
        const name =
            detail.name ||
            parseStepLabel(existingTitle || '').name ||
            'Untitled step'
        return {
            ...base,
            title: `${version} ${name}`.trim(),
        }
    }

    if (module === 'journey') {
        const journeyNumber = (() => {
            const match = String(existingTitle || '').match(/^(\d+)\./)
            return match ? Number(match[1]) : 1
        })()
        const name = detail.name || parseJourneyLabel(existingTitle || '') || 'Untitled journey'

        return {
            ...base,
            title: `${journeyNumber}. ${name}`,
        }
    }

    if (module === 'release') {
        return {
            ...base,
            title: detail.name || existingTitle || 'Untitled release',
            start: detail.start ?? null,
            end: detail.end ?? null,
            dateRange: formatReleaseDateRange(detail.start, detail.end),
        }
    }

    return {
        ...base,
        ...mapStoryStatusFields(detail),
        title: detail.name || 'Untitled story',
    }
}

export const getStoryMapCard = async (cardId, module) => {
    try {
        const params = new URLSearchParams({
            module: String(module),
        })

        const response = await axiosNew({
            url: `support/storyMap/get-card/${cardId}?${params.toString()}`,
            method: 'get',
        })

        const payload = response?.data ?? {}
        const detail = payload.result ?? payload.data

        if (!detail?._id && !detail?.id) {
            return {
                success: false,
                detail: null,
                message: formatStoryMapMessage(payload.message, 'Card not found'),
            }
        }

        return {
            success: payload.success ?? true,
            detail,
            message: formatStoryMapMessage(payload.message, 'Card loaded successfully'),
        }
    } catch (error) {
        return {
            success: false,
            detail: null,
            message: formatStoryMapMessage(
                error?.response?.data?.message,
                formatStoryMapMessage(error?.message, 'Failed to load card')
            ),
        }
    }
}

const mapStoryMapComment = (item) => ({
    id: String(item?._id ?? item?.id ?? ''),
    author: item?.username || 'Anonymous',
    text: item?.content || '',
    createdAt: item?.createdAt
        ? dayjs(item.createdAt).format('D MMM YYYY')
        : '',
})

const mapActivityAttachment = (attachment) => {
    if (!attachment) return null

    return {
        fileKey: attachment.fileKey || '',
        fileName: attachment.fileName || 'Attachment',
        fileType: attachment.fileType || '',
        url: attachment.url || '',
    }
}

const mapStoryMapActivity = (item) => {
    const rawDate = item?.createdAt || (item?.time ? new Date(item.time) : null)

    return {
        id: String(item?._id ?? item?.id ?? ''),
        description: item?.description || '',
        action: item?.action || '',
        field: item?.field ?? null,
        oldValue: item?.oldValue ?? null,
        newValue: item?.newValue ?? null,
        newAttachment: mapActivityAttachment(item?.newAttachment),
        oldAttachment: mapActivityAttachment(item?.oldAttachment),
        content: item?.content ?? item?.commentText ?? null,
        moduleType: item?.moduleType || '',
        authorName: item?.createdBy?.name || 'Unknown',
        authorId: item?.createdBy?._id ? String(item.createdBy._id) : '',
        authorAvatar: item?.createdBy?.profilePicture || '',
        createdAtRaw: rawDate ? dayjs(rawDate).valueOf() : 0,
        dateKey: rawDate ? dayjs(rawDate).format('YYYY-MM-DD') : '',
        dateLabel: rawDate ? dayjs(rawDate).format('DD.MM.YYYY') : '',
        createdAt: rawDate
            ? dayjs(rawDate).format('DD.MM.YYYY, h:mm:ss A')
            : '',
    }
}

export const listStoryMapComments = async (
    moduleId,
    { limit = 10, skip = 0 } = {}
) => {
    try {
        const params = new URLSearchParams({
            limit: String(limit),
            skip: String(skip),
        })

        const response = await axiosNew({
            url: `support/storyMap/comment/list/${moduleId}?${params.toString()}`,
            method: 'get',
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? true,
            isNext: payload.isNext ?? false,
            comments: (payload.data ?? []).map(mapStoryMapComment),
            message: payload.message,
        }
    } catch (error) {
        return {
            success: false,
            isNext: false,
            comments: [],
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to load comments',
        }
    }
}

export const listStoryMapActivity = async (
    moduleId,
    { limit = 20, skip = 0, from, to } = {}
) => {
    try {
        const params = new URLSearchParams({
            limit: String(limit),
            skip: String(skip),
        })

        if (from) params.set('from', String(from))
        if (to) params.set('to', String(to))

        const response = await axiosNew({
            url: `support/storyMap/activity/list/${moduleId}?${params.toString()}`,
            method: 'get',
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? true,
            isNext: payload.isNext ?? false,
            activities: (payload.data ?? []).map(mapStoryMapActivity),
            message: payload.message,
        }
    } catch (error) {
        return {
            success: false,
            isNext: false,
            activities: [],
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to load activity',
        }
    }
}

export const addStoryMapComment = async ({
    username = 'Anonymous',
    content,
    storyMapId,
    moduleId,
    moduleType,
} = {}) => {
    try {
        const response = await axiosNew({
            url: 'support/storyMap/comment/add',
            data: {
                username,
                content,
                storyMapId,
                moduleId,
                moduleType,
            },
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result),
            result: payload.result ?? payload.data,
            message: payload.message ?? 'Comment added successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to add comment',
        }
    }
}

export const deleteStoryMapComment = async (commentId) => {
    try {
        if (!commentId) {
            return {
                success: false,
                result: null,
                message: 'Comment id is required',
            }
        }

        const response = await axiosNew({
            url: `support/storyMap/comment/delete/${commentId}`,
            method: 'delete',
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result),
            result: payload.result ?? payload.data,
            message: payload.message ?? 'Comment deleted successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to delete comment',
        }
    }
}

export const editStoryMapComment = async (
    commentId,
    { content, username } = {}
) => {
    try {
        if (!commentId) {
            return {
                success: false,
                result: null,
                message: 'Comment id is required',
            }
        }

        const text = String(content ?? '').trim()
        if (!text) {
            return {
                success: false,
                result: null,
                message: 'Comment content is required',
            }
        }

        const response = await axiosNew({
            url: `support/storyMap/comment/edit/${commentId}`,
            method: 'put',
            data: {
                content: text,
                ...(username ? { username } : {}),
            },
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result),
            result: payload.result ?? payload.data,
            message: payload.message ?? 'Comment updated successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to update comment',
        }
    }
}

export const exportStoryMapExcel = async (
    storyMapId,
    { fileName } = {}
) => {
    try {
        if (!storyMapId) {
            return {
                success: false,
                message: 'Story map id is required',
            }
        }

        const response = await axiosNew({
            url: 'support/storyMap/excel',
            method: 'post',
            data: { storyMapId: String(storyMapId) },
            responseType: 'blob',
        })

        const contentType = response?.headers?.['content-type'] || ''
        if (contentType.includes('application/json')) {
            const text = await response.data.text()
            let message = 'Failed to export story map'
            try {
                const json = JSON.parse(text)
                message = formatStoryMapMessage(json?.message, message)
            } catch {
                // keep fallback message
            }
            return { success: false, message }
        }

        let downloadName =
            String(fileName || '').trim() || `StoryMap_${storyMapId}.xlsx`
        const disposition = response?.headers?.['content-disposition'] || ''
        const dispositionMatch = disposition.match(
            /filename\*?=(?:UTF-8''|["']?)([^;"'\n]+)/i
        )
        if (dispositionMatch?.[1]) {
            downloadName = decodeURIComponent(
                dispositionMatch[1].replace(/['"]/g, '')
            )
        }
        if (!/\.xlsx$/i.test(downloadName)) {
            downloadName = `${downloadName}.xlsx`
        }

        const blob = new Blob([response.data], {
            type:
                contentType ||
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = downloadName
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        return { success: true, message: 'Story map exported successfully' }
    } catch (error) {
        let message =
            error?.message || 'Failed to export story map'

        const errorData = error?.response?.data
        if (errorData instanceof Blob) {
            try {
                const text = await errorData.text()
                const json = JSON.parse(text)
                message = formatStoryMapMessage(json?.message, message)
            } catch {
                // keep fallback message
            }
        } else if (errorData?.message) {
            message = formatStoryMapMessage(errorData.message, message)
        }

        return {
            success: false,
            message,
        }
    }
}

export const deleteStoryMap = async (storyMapId) => {
    try {
        const response = await axiosNew({
            url: `support/storyMap/delete/${storyMapId}`,
            method: 'delete',
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result),
            result: payload.result ?? payload.data,
            message: payload.message ?? 'Story map deleted successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to delete story map',
        }
    }
}

export const createStoryMap = async ({
    name = 'Untitled Story Map',
    projectId,
} = {}) => {
    try {
        const response = await axiosNew({
            url: 'support/storyMap/add',
            data: {
                name,
                projectId,
            },
        })

        const payload = response?.data ?? {}

        return {
            success: payload.success ?? Boolean(payload.result),
            result: payload.result ?? payload.data,
            message: payload.message ?? 'Story Map created successfully',
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message:
                error?.response?.data?.message ??
                error?.message ??
                'Failed to create story map',
        }
    }
}

export const buildStoryMapBoardLink = ({ projectId, storyMapId }) => {
    if (!projectId || !storyMapId) return ''
    if (!isMongoObjectId(storyMapId)) return ''

    const path = PRIVATE_ROUTES.projects.projectIdView(projectId, 'storyMap')
    const params = new URLSearchParams({ storyMapId: String(storyMapId) })
    return `${window.location.origin}${path}?${params.toString()}`
}

export const buildStoryMapCardLink = ({
    projectId,
    storyMapId,
    cardType,
    cardId,
}) => {
    if (!projectId || !storyMapId || !cardType || !cardId) return ''
    if (!isMongoObjectId(storyMapId) || !isMongoObjectId(cardId)) return ''
    if (cardType !== 'step' && cardType !== 'story') return ''

    const path = PRIVATE_ROUTES.projects.projectIdView(projectId, 'storyMap')
    const params = new URLSearchParams({ storyMapId: String(storyMapId) })
    if (cardType === 'step') {
        params.set('stepId', String(cardId))
    } else {
        params.set('storyId', String(cardId))
    }

    return `${window.location.origin}${path}?${params.toString()}`
}

export const copyStoryMapLinkToClipboard = async (text) => {
    try {
        if (!text) return false
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text)
            return true
        }
    } catch {
        // fall through
    }

    try {
        const el = document.createElement('textarea')
        el.value = String(text)
        el.setAttribute('readonly', '')
        el.style.position = 'fixed'
        el.style.top = '-1000px'
        el.style.left = '-1000px'
        document.body.appendChild(el)
        el.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(el)
        return ok
    } catch {
        return false
    }
}
